import { applySeo } from "@/lib/seo-head";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { ArrowRight, Scale, Package, Newspaper } from "lucide-react";
import { useT, useMedia } from "@/lib/i18n";
import servicesHero from "@/assets/services-hero.jpg";

export const Route = createFileRoute("/services/")({
  component: ServicesIndex,
  head: (ctx) => applySeo(ctx.matches, ((_c: unknown) => ({
    meta: [
      { title: "Servicios — RR Lex Juristas" },
      { name: "description", content: "Áreas de práctica, paquetes con precio cerrado y casos en los medios del despacho RR Lex Juristas." },
      { property: "og:title", content: "Servicios — RR Lex Juristas" },
      { property: "og:description", content: "Defensa penal, derecho de familia, mercantil, paquetes con precio cerrado y casos reconocidos." },
      { property: "og:url", content: "https://rrlexjuristas.com/services" },
    ],
    links: [{ rel: "canonical", href: "https://rrlexjuristas.com/services" }],
  }))(ctx)),
});

function ServicesIndex() {
  const { tk } = useT();
  const heroImg = useMedia("servicios.hero", servicesHero, "Áreas de práctica");

  const cards = [
    {
      to: "/services/areas" as const,
      icon: Scale,
      kicker: tk("services.index.areas.kicker", "Áreas de práctica"),
      title: tk("services.index.areas.title", "Todas nuestras especialidades"),
      desc: tk("services.index.areas.desc", "Más de 20 áreas del derecho con subespecialidades detalladas para cada situación."),
    },
    {
      to: "/services/paquetes" as const,
      icon: Package,
      kicker: tk("services.index.paquetes.kicker", "Paquetes"),
      title: tk("services.index.paquetes.title", "Trámites con precio cerrado"),
      desc: tk("services.index.paquetes.desc", "Servicios concretos con alcance definido y precio único. Sin sorpresas."),
    },
    {
      to: "/services/casos" as const,
      icon: Newspaper,
      kicker: tk("services.index.casos.kicker", "Casos en los medios"),
      title: tk("services.index.casos.title", "Procedimientos reconocidos"),
      desc: tk("services.index.casos.desc", "Casos con cobertura en prensa nacional, respetando siempre el secreto profesional."),
    },
  ];

  return (
    <Layout>
      <section className="relative border-b border-gold/15 bg-cover bg-center" style={{ backgroundImage: `url(${heroImg.url})` }}>
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 text-center">
          <span className="text-xs tracking-[0.4em] uppercase text-gold">{tk("servicios.feb6e5", "Servicios y Paquetes")}</span>
          <h1 className="font-serif text-5xl md:text-7xl mt-5 max-w-4xl mx-auto text-white">
            {tk("servicios.2c8b5c", "Nuestros Servicios ")}
            <em className="not-italic gold-gradient">{tk("servicios.eeb559", "Jurídicos")}</em>
          </h1>
          <p className="mt-8 text-white/80 max-w-2xl mx-auto leading-relaxed">
            {tk("servicios.7aa9ba", "Descubre nuestras áreas de especialización y nuestros paquetes cerrados con precio único para trámites concretos.")}
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.to}
                to={c.to}
                className="group rounded-2xl border border-gold/20 bg-card/30 p-8 flex flex-col hover:border-gold/60 hover:bg-card/60 transition-colors"
              >
                <div className="flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-gold/80">
                  <Icon size={16} className="text-gold" />
                  <span>{c.kicker}</span>
                </div>
                <h2 className="font-serif text-2xl md:text-3xl mt-5 group-hover:text-gold transition-colors">{c.title}</h2>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed flex-1">{c.desc}</p>
                <div className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-gold/80 group-hover:text-gold">
                  {tk("services.index.cta", "Ver más")} <ArrowRight size={12} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </Layout>
  );
}
